import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { FaceAnalysis } from './analyzeFace';
interface ScoreCardProps {
  analysis: FaceAnalysis;
}
export function ScoreCard({
  analysis
}: ScoreCardProps) {
  const {
    t
  } = useLanguage();
  const {
    overallScore,
    subScores
  } = analysis;
  // Circle geometry
  const radius = 78;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - overallScore / 100 * circumference;
  const bars = [{
    label: t('faceAnalysis.results.facialBalance'),
    value: subScores.facialBalance
  }, {
    label: t('faceAnalysis.results.volumeBalance'),
    value: subScores.volumeBalance
  }, {
    label: t('faceAnalysis.results.profileBalance'),
    value: subScores.profileBalance
  }, {
    label: t('faceAnalysis.results.expressionHarmony'),
    value: subScores.expressionHarmony
  }, {
    label: t('faceAnalysis.results.skinShadow'),
    value: subScores.skinShadow
  }];
  return <div className="bg-white rounded-3xl shadow-xl shadow-brand-copper/5 border border-brand-lightCopper/20 overflow-hidden" data-id="element-451">
      <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-452">
        {/* Score circle */}
        <div className="flex flex-col items-center text-center" data-id="element-453">
          <div className="relative w-48 h-48 mb-3" data-id="element-454">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 180 180" data-id="element-455">
              <circle cx="90" cy="90" r={radius} fill="none" stroke="#f3f4f6" strokeWidth="12" data-id="element-456" />
              <motion.circle cx="90" cy="90" r={radius} fill="none" stroke="url(#scoreGradient)" strokeWidth="12" strokeLinecap="round" initial={{
              strokeDashoffset: circumference
            }} animate={{
              strokeDashoffset: offset
            }} transition={{
              duration: 1.5,
              ease: 'easeOut'
            }} style={{
              strokeDasharray: circumference
            }} data-id="element-457" />
              <defs data-id="element-458">
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-459">
                  <stop offset="0%" stopColor="#c79268" data-id="element-460" />
                  <stop offset="100%" stopColor="#9a6b48" data-id="element-461" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-462">
              <motion.div initial={{
              opacity: 0,
              scale: 0.5
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.6
            }} className="flex items-baseline" data-id="element-463">
                <span className="text-5xl font-light text-gray-900" data-id="element-464">
                  {overallScore}
                </span>
                <span className="text-xl text-gray-400 ml-1" data-id="element-465">/100</span>
              </motion.div>
              <div className="text-xs text-brand-copper tracking-wider uppercase mt-1 font-semibold flex items-center" data-id="element-466">
                <Sparkles className="w-3 h-3 mr-1" data-id="element-467" />
                {t('faceAnalysis.results.overallScore')}
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 max-w-xs leading-relaxed" data-id="element-468">
            {t('faceAnalysis.results.overallScoreDesc')}
          </p>
        </div>

        {/* Sub-scores */}
        <div className="space-y-3.5" data-id="element-469">
          {bars.map((b, idx) => <motion.div key={b.label} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3 + idx * 0.08
        }} data-id="element-470">
              <div className="flex justify-between items-center mb-1.5" data-id="element-471">
                <span className="text-sm text-gray-700 font-medium" data-id="element-472">
                  {b.label}
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-473">
                  {b.value}
                </span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-474">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${b.value}%`
            }} transition={{
              duration: 1,
              delay: 0.4 + idx * 0.08,
              ease: 'easeOut'
            }} className="h-full bg-gradient-to-r from-brand-copper to-brand-darkCopper rounded-full" data-id="element-475" />
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}